import React, {
  createContext,
  useReducer,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";

const UPDATE_SELECTED_COLORS_FILTERS = "UPDATE_COLORS_FILTERS";
const UPDATE_SELECTED_MATERIALS_FILTERS = "UPDATE_MATERIALS_FILTERS";
const UPDATE_SELECTED_STYLES_FILTERS = "UPDATE_STYLES_FILTERS";
const UPDATE_SELECTED_DIMENSIONS_FILTERS = "UPDATE_DIMENSIONS_FILTERS";
const RESET_SELECTED_FILTERS = "RESET_FILTERS";
const RESET_SELECTED_FILTERS_EXCEPT_DIMENSIONS =
  "RESET_FILTERS_EXCEPT_DIMENSIONS";

interface Filters {
  colorsFilters: string[];
  materialsFilters: string[];
  stylesFilters: string[];
  dimensionsFilters: string[];
}

interface SelectedFilters {
  selectedColorsFilters: string[];
  selectedMaterialsFilters: string[];
  selectedStylesFilters: string[];
  selectedDimensionsFilters: string[];
}

type FilterAction =
  | { type: typeof UPDATE_SELECTED_COLORS_FILTERS; payload: string[] }
  | { type: typeof UPDATE_SELECTED_MATERIALS_FILTERS; payload: string[] }
  | { type: typeof UPDATE_SELECTED_STYLES_FILTERS; payload: string[] }
  | { type: typeof UPDATE_SELECTED_DIMENSIONS_FILTERS; payload: string[] }
  | { type: typeof RESET_SELECTED_FILTERS }
  | { type: typeof RESET_SELECTED_FILTERS_EXCEPT_DIMENSIONS };

interface FilterContextType {
  // Available filters
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;

  // Selected filters
  selectedFilters: SelectedFilters;

  updateSelectedColorsFilters: (filters: string[]) => void;
  updateSelectedMaterialsFilters: (filters: string[]) => void;
  updateSelectedStylesFilters: (filters: string[]) => void;
  updateSelectedDimensionsFilters: (filters: string[]) => void;
  resetSelectedFilters: () => void;
  resetSelectedFiltersExceptDimensions: () => void;

  toggleColorFilter: (filter: string) => void;
  toggleMaterialFilter: (filter: string) => void;
  toggleStyleFilter: (filter: string) => void;
  setDimensionFilter: (filter: string) => void; // Usually single selection

  hasActiveFilters: boolean;
  activeFiltersCount: number;
}

interface FilterProviderProps {
  children: ReactNode;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

const initialSelectedFilters: SelectedFilters = {
  selectedColorsFilters: [],
  selectedMaterialsFilters: [],
  selectedStylesFilters: [],
  selectedDimensionsFilters: [],
};

const initialFilters: Filters = {
  colorsFilters: [],
  materialsFilters: [],
  stylesFilters: [],
  dimensionsFilters: [],
};

function filtersReducer(
  state: SelectedFilters,
  action: FilterAction
): SelectedFilters {
  switch (action.type) {
    case UPDATE_SELECTED_COLORS_FILTERS:
      return { ...state, selectedColorsFilters: action.payload };
    case UPDATE_SELECTED_MATERIALS_FILTERS:
      return { ...state, selectedMaterialsFilters: action.payload };
    case UPDATE_SELECTED_STYLES_FILTERS:
      return { ...state, selectedStylesFilters: action.payload };
    case UPDATE_SELECTED_DIMENSIONS_FILTERS:
      return { ...state, selectedDimensionsFilters: action.payload };
    case RESET_SELECTED_FILTERS_EXCEPT_DIMENSIONS:
      return {
        ...initialSelectedFilters,
        selectedDimensionsFilters: state.selectedDimensionsFilters,
      };
    case RESET_SELECTED_FILTERS:
      return initialSelectedFilters;
    default:
      return state;
  }
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
  const [filters, setFilters] = useState<Filters>(initialFilters);
  const [selectedFilters, selectedFiltersDispatch] = useReducer(
    filtersReducer,
    initialSelectedFilters
  );

  const updateSelectedColorsFilters = useCallback((filters: string[]) => {
    selectedFiltersDispatch({
      type: UPDATE_SELECTED_COLORS_FILTERS,
      payload: filters,
    });
  }, []);

  const updateSelectedMaterialsFilters = useCallback((filters: string[]) => {
    selectedFiltersDispatch({
      type: UPDATE_SELECTED_MATERIALS_FILTERS,
      payload: filters,
    });
  }, []);

  const updateSelectedStylesFilters = useCallback((filters: string[]) => {
    selectedFiltersDispatch({
      type: UPDATE_SELECTED_STYLES_FILTERS,
      payload: filters,
    });
  }, []);

  const updateSelectedDimensionsFilters = useCallback((filters: string[]) => {
    selectedFiltersDispatch({
      type: UPDATE_SELECTED_DIMENSIONS_FILTERS,
      payload: filters,
    });
  }, []);

  const resetSelectedFilters = useCallback(() => {
    selectedFiltersDispatch({ type: RESET_SELECTED_FILTERS });
  }, []);

  const resetSelectedFiltersExceptDimensions = useCallback(() => {
    selectedFiltersDispatch({ type: RESET_SELECTED_FILTERS_EXCEPT_DIMENSIONS });
  }, []);

  const toggleColorFilter = useCallback(
    (filter: string) => {
      const { selectedColorsFilters } = selectedFilters;
      const updatedFilters = selectedColorsFilters.includes(filter)
        ? selectedColorsFilters.filter((f) => f !== filter)
        : [...selectedColorsFilters, filter];
      updateSelectedColorsFilters(updatedFilters);
    },
    [selectedFilters.selectedColorsFilters, updateSelectedColorsFilters]
  );

  const toggleMaterialFilter = useCallback(
    (filter: string) => {
      const { selectedMaterialsFilters } = selectedFilters;
      const updatedFilters = selectedMaterialsFilters.includes(filter)
        ? selectedMaterialsFilters.filter((f) => f !== filter)
        : [...selectedMaterialsFilters, filter];
      updateSelectedMaterialsFilters(updatedFilters);
    },
    [selectedFilters.selectedMaterialsFilters, updateSelectedMaterialsFilters]
  );

  const toggleStyleFilter = useCallback(
    (filter: string) => {
      const { selectedStylesFilters } = selectedFilters;
      const updatedFilters = selectedStylesFilters.includes(filter)
        ? selectedStylesFilters.filter((f) => f !== filter)
        : [...selectedStylesFilters, filter];
      updateSelectedStylesFilters(updatedFilters);
    },
    [selectedFilters.selectedStylesFilters, updateSelectedStylesFilters]
  );

  const setDimensionFilter = useCallback(
    (filter: string) => {
      updateSelectedDimensionsFilters([filter]);
    },
    [updateSelectedDimensionsFilters]
  );

  const hasActiveFilters = useMemo(() => {
    return (
      selectedFilters.selectedColorsFilters.length > 0 ||
      selectedFilters.selectedMaterialsFilters.length > 0 ||
      selectedFilters.selectedStylesFilters.length > 0 ||
      selectedFilters.selectedDimensionsFilters.length > 0
    );
  }, [selectedFilters]);

  const activeFiltersCount = useMemo(() => {
    return (
      selectedFilters.selectedColorsFilters.length +
      selectedFilters.selectedMaterialsFilters.length +
      selectedFilters.selectedStylesFilters.length +
      selectedFilters.selectedDimensionsFilters.length
    );
  }, [selectedFilters]);

  const contextValue: FilterContextType = useMemo(
    () => ({
      filters,
      setFilters,
      selectedFilters,
      updateSelectedColorsFilters,
      updateSelectedMaterialsFilters,
      updateSelectedStylesFilters,
      updateSelectedDimensionsFilters,
      resetSelectedFilters,
      resetSelectedFiltersExceptDimensions,
      toggleColorFilter,
      toggleMaterialFilter,
      toggleStyleFilter,
      setDimensionFilter,
      hasActiveFilters,
      activeFiltersCount,
    }),
    [
      filters,
      setFilters,
      selectedFilters,
      updateSelectedColorsFilters,
      updateSelectedMaterialsFilters,
      updateSelectedStylesFilters,
      updateSelectedDimensionsFilters,
      resetSelectedFilters,
      resetSelectedFiltersExceptDimensions,
      toggleColorFilter,
      toggleMaterialFilter,
      toggleStyleFilter,
      setDimensionFilter,
      hasActiveFilters,
      activeFiltersCount,
    ]
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterContext;
export type { FilterContextType, Filters, SelectedFilters };
